import React, { useState, useEffect } from 'react';
import Select, { SingleValue, components, OptionProps } from 'react-select';
import { useTranslation } from 'react-i18next';
import { useIsReadOnly } from '../../hooks/useIsReadOnly';
import { formatDate as formatDateCentral } from '../../utils/formatters';
import { reactSelectTokenStyles } from '../../styles/reactSelectTokenStyles';

// Interface für Invoice Draft Items
interface InvoiceDraftItem {
  id: string;
  draftNumber: string;
  buyerName: string;
  totalAmount: number;
  currency: string;
  type: 'zugferd' | 'xrechnung';
  createdAt: string | Date;
  lastModified?: string | Date;
  createdDate?: string | Date;
}

// Interface für react-select Optionen
interface InvoiceDraftOption {
  value: string; // Draft-ID
  label: string; // Anzeigetext
}

// Erweiterte Option-Struktur für das Custom-Rendering
interface ExtendedInvoiceDraftOption extends InvoiceDraftOption {
  draftData: InvoiceDraftItem;
}

interface InvoiceDraftSelectorProps {
  drafts: InvoiceDraftItem[];
  selectedDraftId: string | null;
  onSelectDraft: (selectedOption: SingleValue<InvoiceDraftOption>) => void;
  onDeleteDraft: (draftId: string) => void;
  isLoading: boolean;
  error: string | null;
  title?: string;
  filterType?: 'zugferd' | 'xrechnung' | null; // Filter für Rechnungstyp
}

const InvoiceDraftSelector: React.FC<InvoiceDraftSelectorProps> = ({
  drafts,
  selectedDraftId,
  onSelectDraft,
  onDeleteDraft,
  isLoading,
  error,
  title,
  filterType = null
}) => {
  const { t, ready, i18n } = useTranslation('invoice');
  // M11b-3: advisor (read-only) darf keine Entwürfe löschen.
  const isReadOnly = useIsReadOnly();
  // Filtere Drafts nach Typ, falls spezifiziert
  const filteredDrafts = filterType 
    ? drafts.filter(draft => draft.type === filterType)
    : drafts;

  // Sortiere Drafts nach Datum (neueste zuerst)
  const sortedDrafts = [...filteredDrafts].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.lastModified || a.createdDate || 0);
    const dateB = new Date(b.createdAt || b.lastModified || b.createdDate || 0);
    return dateB.getTime() - dateA.getTime(); // Absteigende Sortierung (neueste zuerst)
  });

  // Custom Option Component mit Lösch-Button
  const CustomOption: React.FC<OptionProps<ExtendedInvoiceDraftOption, false>> = (props) => {
    const { data, innerRef, innerProps } = props;
    
    const handleDeleteClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDeleteDraft(data.draftData.id);
    };

    // Icon für Rechnungstyp
    const getTypeIcon = (type: string) => {
      switch (type) {
        case 'zugferd':
          return '📄'; // PDF Icon
        case 'xrechnung':
          return '📝'; // XML Icon
        default:
          return '📋';
      }
    };

    return (
      <div ref={innerRef} {...innerProps} className="flex items-center justify-between px-3 py-2 hover:bg-surface-hover cursor-pointer">
        <div className="flex items-center flex-grow text-primary">
          <span className="mr-2">{getTypeIcon(data.draftData.type)}</span>
          <span>{data.label}</span>
        </div>
        {!isReadOnly && (
        <button
          onClick={handleDeleteClick}
          className="ml-2 p-1 hover:bg-red-600 text-muted hover:text-white rounded transition-colors group"
          title="Entwurf löschen"
        >
          <svg 
            className="w-4 h-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
            />
          </svg>
        </button>
        )}
      </div>
    );
  };

  // Optionen für react-select
  const draftSelectOptions: ExtendedInvoiceDraftOption[] = sortedDrafts.map((draft: InvoiceDraftItem) => {
    // Versuche verschiedene Datumsfelder zu finden
    const dateStr = draft.createdAt || draft.lastModified || draft.createdDate;
    let formattedDate = 'Unbekanntes Datum';
    
    if (dateStr) {
      const formatted = formatDateCentral(dateStr, { locale: i18n.language, includeTime: true });
      if (formatted !== '—') {
        formattedDate = formatted;
      }
    }

    // Bessere Label-Formatierung
    const customerName = draft.buyerName || 'Unbekannter Kunde';
    const amount = draft.totalAmount ? `${draft.totalAmount.toFixed(2)} €` : 'Unbekannter Betrag';
    const typeLabel = draft.type === 'zugferd' ? 'ZUGFeRD' : 'XRechnung';
    
    return {
      value: draft.id,
      label: `${customerName} - ${amount} (${typeLabel}, ${formattedDate})`,
      draftData: draft
    };
  });

  // Aktuell ausgewählte Option für react-select
  const selectedDraftOption = draftSelectOptions.find(option => option.value === selectedDraftId);

    // Warte bis Übersetzungen geladen sind
  if (!ready) {
    return (
      <div className="bg-surface-raised shadow-lg rounded-lg p-4 border border-hairline">
        <div className="animate-pulse">
          <div className="h-6 bg-surface-hover rounded mb-3 w-64"></div>
          <div className="h-10 bg-surface-hover rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-raised shadow-lg rounded-lg p-4 border border-hairline">
      <h2 className="text-lg font-semibold text-primary mb-3">
        {title || (filterType === 'xrechnung' ? t('draft.useXRechnungAsBasis') : t('draft.useZugferdAsBasis'))}
      </h2>
      <div className="flex items-center space-x-4">
        <div className="flex-grow">
          <Select<ExtendedInvoiceDraftOption> 
            className="react-select-container"
            classNamePrefix="react-select"
            options={draftSelectOptions}
            value={selectedDraftOption || null}
            onChange={onSelectDraft}
            components={{ Option: CustomOption }}
            placeholder={t('draft.selectSaved')}
            isLoading={isLoading}
            isClearable={true}
            noOptionsMessage={() => isLoading ? t('loadingDrafts', { ns: 'common' }) : t('noDraftsFound', { ns: 'common' })}
            styles={{
              control: (base, state) => ({
                ...(reactSelectTokenStyles.control?.(base, state) ?? base),
                minHeight: '48px', // Noch größer für mobile
                fontSize: '16px', // Verhindert Zoom auf iOS
              }),
              menu: (base, state) => ({
                ...(reactSelectTokenStyles.menu?.(base, state) ?? base),
                zIndex: 9999,
                fontSize: '16px',
              }),
              option: (base) => ({
                ...base,
                backgroundColor: 'transparent', // Wird von CustomOption gesteuert
                padding: 0, // Entferne Standard-Padding da CustomOption das handhabt
                cursor: 'pointer'
              }),
              singleValue: (base, state) => ({
                ...(reactSelectTokenStyles.singleValue?.(base, state) ?? base),
                fontSize: '16px',
              }),
              input: (base, state) => ({
                ...(reactSelectTokenStyles.input?.(base, state) ?? base),
                fontSize: '16px',
                margin: 0,
                paddingLeft: 0,
                paddingRight: 0,
              }),
              placeholder: (base, state) => ({
                ...(reactSelectTokenStyles.placeholder?.(base, state) ?? base),
                fontSize: '16px',
              }),
              valueContainer: (base) => ({
                ...base,
                padding: '8px 12px',
              }),
              indicatorsContainer: (base) => ({
                ...base,
                padding: '8px',
              })
            }}
          />
          {error && <p className="text-[var(--tone-danger-fg)] text-sm mt-1">{error}</p>}
        </div>
      </div>
      
      {selectedDraftId && (
        <div className="mt-2 text-sm text-muted">
          <span className="font-medium text-primary">{t('draft.selected')}:</span> {selectedDraftOption?.label}
        </div>
      )}
      
      {sortedDrafts.length === 0 && !isLoading && (
        <div className="mt-2 text-sm text-muted">
          {t('noDraftsSaved', { ns: 'common', type: filterType ? (filterType === 'zugferd' ? 'ZUGFeRD-' : 'XRechnung-') : '' })}
        </div>
      )}
    </div>
  );
};

export default InvoiceDraftSelector;